import { useEffect, useState } from 'react';

function resolveTokens(tokenNames: readonly string[]): Record<string, string> {
  if (typeof document === 'undefined') {
    return {};
  }
  const styles = getComputedStyle(document.documentElement);
  const resolved: Record<string, string> = {};
  for (const name of tokenNames) {
    resolved[name] = styles.getPropertyValue(name).trim();
  }
  return resolved;
}

/**
 * REV18 (docs/specs/REV18-switch-classnames-toast-theme.md) — resolves CSS custom properties
 * (design tokens, e.g. `--accent`) on `<html>` to literal color strings, for third-party
 * components (react-switch) that only accept real colors, not `var(--x)` references.
 *
 * Re-resolves live whenever the theme or accent changes: `applyTheme` (lib/theme.ts) writes
 * `data-theme` and `applyAccent` (lib/accent.ts) writes an inline `--accent` override, both onto
 * `document.documentElement` — so a `MutationObserver` watching that element's `data-theme`/
 * `style` attributes catches every one of the 17 themes x accent-override combinations without a
 * page reload. Pass a stable (module-level or `useMemo`'d) array — a fresh array literal on every
 * render still works (the dependency is a joined string, not the array reference) but re-renders
 * more than necessary.
 */
export function useResolvedTokens(tokenNames: readonly string[]): Record<string, string> {
  const [tokens, setTokens] = useState<Record<string, string>>(() => resolveTokens(tokenNames));
  const namesKey = tokenNames.join(',');

  useEffect(() => {
    setTokens(resolveTokens(tokenNames));

    const observer = new MutationObserver(() => {
      setTokens(resolveTokens(tokenNames));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'style'],
    });

    return () => observer.disconnect();
    // `tokenNames` is intentionally represented by `namesKey` here — see the doc comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesKey]);

  return tokens;
}
